import clsx from "clsx"
import { makeStyles } from "@material-ui/core"
import { FC } from "react"

export type DBTYesNoValue = "yes" | "no"
export type DBTYesNoPalette = "status" | "boolean"

type PaletteColor = {
  background: string
  text: string
  border?: string
  borderWidth?: number
  fontWeight?: number
}

export interface DBTYesNoIconProps {
  /** Controls which pill variant to render. */
  value: DBTYesNoValue
  /** Optional override for the rendered text. Defaults to "Yes"/"No". */
  label?: string
  /** Selects the color palette from the DBT designs. */
  palette?: DBTYesNoPalette
  className?: string
}

const YES_NO_PALETTES: Record<
  DBTYesNoPalette,
  Record<DBTYesNoValue, PaletteColor>
> = {
  status: {
    yes: {
      background: "#EAFFF7",
      border: "rgba(0, 0, 0, 0.1)",
      text: "#007546",
      borderWidth: 2,
      fontWeight: 700,
    },
    no: {
      background: "#FEF6F7",
      border: "#D33444",
      text: "#D33444",
      borderWidth: 2,
      fontWeight: 700,
    },
  },
  boolean: {
    yes: {
      background: "#7E58C0",
      border: "rgba(97, 53, 175, 0.1)",
      text: "#FFFFFF",
      borderWidth: 2,
      fontWeight: 700,
    },
    no: {
      background: "#F5F5F5",
      border: "transparent",
      text: "#474747",
      borderWidth: 0,
      fontWeight: 600,
    },
  },
}

const useStyles = makeStyles({
  container: {
    width: 37,
    height: 37,
    borderRadius: "50%",
    borderStyle: "solid",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "16px",
    textAlign: "center",
  },
})

const DBTYesNoIcon: FC<DBTYesNoIconProps> = ({
  value,
  label,
  palette = "status",
  className,
}) => {
  const classes = useStyles()
  const paletteConfig = YES_NO_PALETTES[palette][value]

  return (
    <div
      className={clsx(classes.container, className)}
      data-variant={`dbt-yes-no-${value}-${palette}`}
      style={{
        backgroundColor: paletteConfig.background,
        borderColor: paletteConfig.border,
        borderWidth: paletteConfig.borderWidth ?? 2,
      }}
    >
      <span
        className={classes.label}
        style={{
          color: paletteConfig.text,
          fontWeight: paletteConfig.fontWeight,
        }}
      >
        {label ?? (value === "yes" ? "Yes" : "No")}
      </span>
    </div>
  )
}

export default DBTYesNoIcon
